/** Types supported in function-mode signatures (admin + docs). */
export const FUNCTION_PARAM_TYPES = [
  'int',
  'long',
  'float',
  'double',
  'bool',
  'string',
  'char',
  'int[]',
  'long[]',
  'float[]',
  'double[]',
  'bool[]',
  'string[]',
  'char[]',
  'int[][]',
  'long[][]',
  'float[][]',
  'double[][]',
  'bool[][]',
  'string[][]',
  'char[][]',
  // Linked list & tree — stored as arrays in test cases (LeetCode style)
  'ListNode',
  'TreeNode',
  'List<int>',
  'List<long>',
  'List<string>',
  'List<bool>',
  'List<List<int>>',
  'List<List<string>>',
  'List<Map<int,int>>',
  'Map<int,int>',
  'Map<int,long>',
  'Map<int,List<int>>',
  'Map<string,int>',
  'Map<string,string>',
  'Map<string,List<int>>',
  'Map<string,List<string>>',
  'Set<int>',
  'Set<string>',
  'Set<List<int>>',
  'void',
];

/** Autocomplete group label for signature type pickers. */
export const functionTypeGroup = (type) => {
  const t = String(type || '');
  if (t === 'void') return 'Special';
  if (t === 'ListNode' || t === 'TreeNode') return 'Linked list & tree';
  if (t.includes('Map<') || t.includes('Set<') || t.startsWith('List<')) return 'Collections';
  if (t.includes('[]')) return 'Arrays';
  return 'Scalars';
};

/** Hint shown when ListNode / TreeNode is selected. */
export const structTypeHint = (type) => {
  if (type === 'ListNode') {
    return 'Enter values as an array: 1, 2, 3 becomes 1→2→3. Empty list: leave blank or [].';
  }
  if (type === 'TreeNode') {
    return 'Level-order (LeetCode): 3, 9, 20, null, null, 15, 7 — use null for missing children.';
  }
  return '';
};

export const signatureUsesStructNodes = (signature) => {
  const types = [
    signature?.returnType,
    ...(signature?.params || []).map((p) => p.type),
  ].map((t) => String(t || ''));
  return types.some((t) => t === 'ListNode' || t === 'TreeNode');
};

export const DEFAULT_SIGNATURE = {
  className: 'Solution',
  methodName: '',
  params: [{ name: '', type: 'int[]' }],
  returnType: 'int[]',
  outputMode: 'return',
  assertOn: [],
};

/** When true, judge compares post-call param values instead of return value. */
export const isArgsOutputMode = (signature) =>
  String(signature?.returnType || '').toLowerCase() === 'void' ||
  signature?.outputMode === 'args';

export const resolveAssertOn = (signature) => {
  const explicit = Array.isArray(signature?.assertOn)
    ? signature.assertOn.map((n) => String(n).trim()).filter(Boolean)
    : [];
  if (explicit.length > 0) return explicit;
  if (isArgsOutputMode(signature)) {
    return (signature?.params || []).map((p) => p.name).filter(Boolean);
  }
  return [];
};

export const emptyFunctionTestCase = (signature) => {
  const args = {};
  (signature?.params || []).forEach((p) => {
    if (!p?.name) return;
    if (p.type === 'ListNode' || p.type === 'TreeNode') {
      args[p.name] = [];
    }
  });
  return {
    args,
    expected:
      isArgsOutputMode(signature)
        ? {}
        : String(signature?.returnType || '') === 'ListNode' ||
            String(signature?.returnType || '') === 'TreeNode'
          ? []
          : null,
    explanation: '',
    isSample: true,
  };
};

/**
 * When switching to void / args judging, coerce scalar/array/null expected into an object
 * so the UI and save validation match the args-mode shape.
 */
export const normalizeTestCasesForArgsMode = (testCases) =>
  (testCases || []).map((tc) => {
    if (tc.expected && typeof tc.expected === 'object' && !Array.isArray(tc.expected)) {
      return tc;
    }
    return { ...tc, expected: {} };
  });

/** Frontend save-check for function-mode cases. */
export const isFunctionTestCaseValid = (tc, signature) => {
  if (!tc?.args || typeof tc.args !== 'object' || Array.isArray(tc.args)) return false;
  const params = signature?.params || [];
  const paramNames = params.map((p) => p.name).filter(Boolean);
  if (paramNames.length > 0 && paramNames.some((name) => tc.args[name] === undefined || tc.args[name] === null)) {
    return false;
  }

  const valueLooksTyped = (type, value) => {
    const t = String(type || '');
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && (t.includes('<') || t.includes('[]') || t === 'ListNode' || t === 'TreeNode')) {
      return false;
    }
    if (t.startsWith('Map') || t.includes('Map<')) {
      return typeof value === 'object' && !Array.isArray(value);
    }
    if (
      t.startsWith('Set') ||
      t.startsWith('List') ||
      t.endsWith('[]') ||
      t === 'ListNode' ||
      t === 'TreeNode'
    ) {
      return Array.isArray(value);
    }
    return true;
  };

  if (params.some((p) => !valueLooksTyped(p.type, tc.args[p.name]))) return false;

  if (isArgsOutputMode(signature)) {
    if (!tc.expected || typeof tc.expected !== 'object' || Array.isArray(tc.expected)) return false;
    const assertOn = resolveAssertOn(signature);
    if (assertOn.length === 0) return true;
    return assertOn.every((name) => {
      const value = tc.expected[name];
      if (value === undefined || value === null || value === '') return false;
      const param = params.find((p) => p.name === name);
      return valueLooksTyped(param?.type || 'int', value);
    });
  }

  if (tc.expected === undefined || tc.expected === null || tc.expected === '') return false;
  return valueLooksTyped(signature?.returnType || 'int', tc.expected);
};

export const emptyIoTestCase = () => ({
  input: '',
  output: '',
  explanation: '',
  isSample: true,
});

/**
 * True when admin should default test cases to JSON editor.
 * Matrices / nested lists stay on simple typed fields ([[1,2],[3,4]] or row form).
 * Maps (and Set of non-scalars) still prefer the full JSON editor.
 */
export const isComplexFunctionType = (type) => {
  const t = String(type || '');
  return (
    t.includes('Map<') ||
    t.includes('Set<') ||
    t.includes('List<Map')
  );
};
